import { ClientEndpoint, Device, DrakewellClientEndpoint, EmailClientEndpoint, ITestEndpointCommand, ITestEndpointCommandParams, NtcipDmsClientEndpoint, TestEndpointCommand, TestEndpointCommandParams, WebRelayClientEndpoint } from "@app/shared";
import { ChangeEvent, useEffect } from "react";
import { CheckboxInput, Input, InputRow, NumericInput, Select } from "../../shared";
import { hasEndpointTest, TEST_ENDPOINT_FIRMWARE_VERSION } from "../../../helpers";

type EditEndpointTestCommandProps = {
    endpoint: ClientEndpoint,
    value: TestEndpointCommand,
    onChange?: (newValue: TestEndpointCommand) => void,
    devices?: Device[]
};

export const TestSupportedEndpointTypes = [
    EmailClientEndpoint.$type,
    WebRelayClientEndpoint.$type,
    NtcipDmsClientEndpoint.$type,
    DrakewellClientEndpoint.$type
]

export const EditEndpointTestCommand: React.FC<EditEndpointTestCommandProps> = (props) => {


    // useEffect to set defaults for the command params based on endpoint type
    useEffect(() => {
        if (props.endpoint.id == props.value.commandParamsJson?.endpointId) {
            //If the endpoint ID matches, do not overwrite existing params
            return;
        }

        var params = new TestEndpointCommandParams({ endpointId: props.endpoint.id } as ITestEndpointCommandParams);

        if (props.endpoint.$type === EmailClientEndpoint.$type) {
            params.testString1 = "Omnisight Test Endpoint Email";
            params.testString2 = "This is a test email to make sure that the email endpoint configuration is properly configured.";
        } else if (props.endpoint.$type === WebRelayClientEndpoint.$type) {
            params.testInt1 = 1; //Relay #
            params.testInt2 = 1; //Duration (seconds)
        } else if (props.endpoint.$type === NtcipDmsClientEndpoint.$type) {
            params.testString1 = "Test Message";
            params.testInt1 = 15; //Display Time (seconds)
        } else if (props.endpoint.$type === DrakewellClientEndpoint.$type) {
            params.testInt1 = 1; //Include sample targets (0/1)
        }

        props.onChange?.(new TestEndpointCommand({
            ...props.value,
            commandParamsJson: params
        } as ITestEndpointCommand));

    }, [props.endpoint, props.value]);

    const handleStrChange = (key: keyof TestEndpointCommandParams, value: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
        var newParams = {
            ...props.value.commandParamsJson,
            [key]: value.target.value
        } as TestEndpointCommandParams;

        props.onChange?.(new TestEndpointCommand({
            ...props.value,
            commandParamsJson: newParams
        } as ITestEndpointCommand));
    };


    const handleIntChange = (key: keyof TestEndpointCommandParams, value?: number) => {
        var newParams = {
            ...props.value.commandParamsJson,
            [key]: value
        } as TestEndpointCommandParams;

        props.onChange?.(new TestEndpointCommand({
            ...props.value,
            commandParamsJson: newParams
        } as ITestEndpointCommand));
    }

    return (
        <>
            {props.endpoint.$type === EmailClientEndpoint.$type && (
                <InputRow>
                    <Input label="Subject" value={props.value.commandParamsJson?.testString1 || ""} onChange={handleStrChange.bind(null, "testString1")} />
                    <Input label="Message" value={props.value.commandParamsJson?.testString2 || ""} onChange={handleStrChange.bind(null, "testString2")} />
                </InputRow>
            )}
            {props.endpoint.$type === WebRelayClientEndpoint.$type && (
                <InputRow>
                    <NumericInput label="Relay #" value={props.value.commandParamsJson?.testInt1 || 0} onChange={handleIntChange.bind(null, "testInt1")} />
                    <NumericInput label="Duration (seconds)" value={props.value.commandParamsJson?.testInt2 || 1} onChange={handleIntChange.bind(null, "testInt2")} />
                </InputRow>
            )}
            {props.endpoint.$type === NtcipDmsClientEndpoint.$type && (
                <InputRow>
                    <Input label="Message" value={props.value.commandParamsJson?.testString1 || ""} onChange={handleStrChange.bind(null, "testString1")} />
                    <NumericInput label="Display Time (seconds)" value={props.value.commandParamsJson?.testInt1 || 15} onChange={handleIntChange.bind(null, "testInt1")} />
                </InputRow>
            )}
            {props.endpoint.$type === DrakewellClientEndpoint.$type && (
                <InputRow>
                    <CheckboxInput
                        label="Include sample targets (otherwise an empty batch is sent)"
                        checked={(props.value.commandParamsJson?.testInt1 ?? 0) !== 0}
                        onChange={(e) => handleIntChange("testInt1", e.target.checked ? 1 : 0)} />
                </InputRow>
            )}
            <InputRow>
                <Select
                    label={`Device to send test command from (must be >= v${TEST_ENDPOINT_FIRMWARE_VERSION})`}
                    value={props.value.deviceId}
                    onChange={(e) => props.onChange?.(new TestEndpointCommand({ ...props.value, deviceId: e.target.value } as ITestEndpointCommand))}
                    options={props.devices?.filter(d => hasEndpointTest(d.firmwareVersion)).map(d => ({ label: d.name, value: d.id })) || []} />
            </InputRow>
        </>
    );
}